import { AxiosResponse } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import type { SWRConfiguration } from "swr";
import useSWR from "swr";
import { useLatest } from "./useLatest";
interface QueryResult<TOutput = any> {
  isRequesting: boolean;
  data: TOutput | undefined;
  error: any;
  status: number | undefined;
  mutate: Function;
}

interface QueryPagantionResult<TItemOutput = any> {
  isRequesting: boolean;
  data: TItemOutput[] | undefined;
  error: any;
  status: number | undefined;
  mutate: Function;
}

type Parameters = Record<string, any>;
type FetchPolicyType = "cache-and-network" | "network-only";


export const usePagantionQuery = <
  TInput = Parameters,
  TItemOutput = any,
  TOutput extends { PageCount?: number | null | undefined; Items?: TItemOutput[] | null | undefined } = any
>(
  inputKey: string,
  fetcher: (queryParams: string) => Promise<AxiosResponse<TOutput>>,
  maxPageIndex?: number
): [QueryPagantionResult<TItemOutput>, (params?: TInput) => void] => {
  const [accumulatedData, setAccumulatedData] = useState<TItemOutput[]>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [paramsString, setParamsString] = useState<string>('');
  const [paramsObj, setParamsObj] = useState<TInput | undefined>(undefined);
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const [finalError, setFinalError] = useState<any>(null);
  const [finalStatus, setFinalStatus] = useState<number | undefined>(undefined);

  const key = shouldFetch ? `${inputKey}-${paramsString}-${pageIndex}` : null;

  const defaultOptions: SWRConfiguration = {
    errorRetryCount: 0,
    refreshInterval: 0,
    errorRetryInterval: 0,
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  };

  const swrFetcher = () => {
    const queryObj = {
      ...(paramsObj || {}) as object,
      pageIndex,
    };
    const queryString = createQueryStringParameters(queryObj);
    return fetcher(queryString);
  };

  const { isValidating, mutate, data: result, error } = useSWR(key, swrFetcher, defaultOptions);

  const handleNewData = useCallback((newData: TOutput) => {
    const newPageCount = maxPageIndex ?? newData?.PageCount ?? 0;
    setPageCount(newPageCount);
    const newItems = newData.Items?.map(item => ({ ...item })) || [];
    setAccumulatedData(prev => [...prev, ...newItems]);
  }, [maxPageIndex]);

  useEffect(() => {
    if (!shouldFetch) return;

    if (result) {
      handleNewData(result.data);
      setFinalStatus(result.status);
      const newPageCount = maxPageIndex ?? result.data.PageCount ?? 0;
      if (result.data.Items?.length === 0 || pageIndex >= newPageCount) {
        setShouldFetch(false);
      } else {
        setPageIndex(prev => prev + 1);
      }
    }
    if (error) {
      setFinalError(error);
      setShouldFetch(false);
    }
  }, [result, error, shouldFetch, handleNewData, pageIndex, maxPageIndex]);

  const doRequest = useCallback((params?: TInput) => {
    const parsedParams = createQueryStringParameters(params ? params : {} as Parameters);
    setParamsString(parsedParams);
    setParamsObj(params ? params : {} as TInput);
    setAccumulatedData([]);
    setPageCount(0);
    setPageIndex(1);
    setFinalError(null);
    setFinalStatus(undefined);
    setShouldFetch(true);
  }, []);

  return [
    {
      isRequesting: isValidating || shouldFetch,
      data: accumulatedData,
      error: finalError || error,
      status: finalStatus || result?.status,
      mutate,
    },
    doRequest,
  ];
}; 


export const useQuery = <TOutput = any, TInput = Parameters>(
  inputKey: string,
  fetcher: (param: string) => Promise<AxiosResponse<any>>,
  fetchPolicy?: FetchPolicyType,
): [QueryResult<TOutput>, (params?: TInput) => void] => {
  let [key, setKey] = useState<string | null>(null);
  let [params, setParams] = useState<string>("");
  let _fetcher = useCallback(() => {
    return fetcher(params);
  }, [fetcher, params]);

  let defaultOptions: SWRConfiguration = {
    errorRetryCount: 0,
    refreshInterval: 0,
    errorRetryInterval: 0,
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  };

  let {
    isValidating,
    mutate,
    data: result,
    error,
  } = useSWR(key, _fetcher, { ...defaultOptions });

  let [data, setData] = useState<TOutput | undefined>(undefined);
  let [status, setStatus] = useState<number | undefined>(undefined);

  let requestedRef = useRef(false);
  const doRequest = useCallback(
    (params?: TInput) => {
      let paramsString = createQueryStringParameters(params as Parameters | undefined);
      setParams(paramsString);
      if (fetchPolicy === "cache-and-network") {
        setKey(inputKey);
      } else {
        let random = Date.now();
        let key = inputKey + random;
        setKey(key);
      }
      setData(undefined);
      setStatus(undefined);
      requestedRef.current = true;
    },
    [fetchPolicy, inputKey],
  );

  // do not revalidate after get data
  let resultRef = useLatest(result);
  let errorRef = useLatest(error);
  useEffect(() => {
    if (!requestedRef.current) return;
    if (isValidating) {
      return;
    }
    if (!resultRef.current && !errorRef.current) return;
    if (resultRef.current) {
      setData(resultRef.current.data);
      setStatus(resultRef.current.status);
    }
    setParams("");
    setKey(null);
  }, [errorRef, isValidating, resultRef]);

  return [
    { isRequesting: isValidating, data, error, status, mutate },
    doRequest,
  ];
};

export const paginationThunk = async <
  TInput = any,
  TItemOutput = any,
  TOutput extends { PageCount?: number | null | undefined; Items?: TItemOutput[] | null | undefined } = any
>(
  fetcher: (queryParams: string) => Promise<AxiosResponse<TOutput>>,
  params?: TInput
): Promise<AxiosResponse<{ Items: TItemOutput[]; PageCount: number }, any>> => {
  let accumulatedData: TItemOutput[] = [];
  let pageIndex = 1;
  let pageCount = 0;
  let lastResponse: AxiosResponse<TOutput> | null = null;

  while (true) {
    const queryString = createQueryStringParameters({ ...(params || {}), pageIndex });
    const response = await fetcher(queryString);
    lastResponse = response;
    const data = response.data;
    if (data) {
      pageCount = data.PageCount ?? 0;
      accumulatedData.push(...(data.Items || []));
      if (!data.Items?.length || pageIndex >= pageCount) {
        break;
      }
      pageIndex++;
    } else {
      break;
    }
  }

  return {
    ...lastResponse,
    data: {
      Items: accumulatedData,
      PageCount: pageCount,
    },
  } as AxiosResponse<{ Items: TItemOutput[]; PageCount: number }, any>;
};

export const infiniteScrollThunk = async <
  TInput = any,
  TItemOutput = any,
  TOutput extends { HasNext?: boolean; Items?: TItemOutput[] | null | undefined; EndCursor?: any } = any
>(
  fetcher: (queryParams: string) => Promise<AxiosResponse<TOutput>>,
  params?: TInput & { startPoint?: string; count?: number; direction?: 'forward' | 'backward' },
  maxItems?: number
): Promise<AxiosResponse<{ Items: TItemOutput[] }, any>> => {
  let accumulatedItems: TItemOutput[] = [];
  let lastResponse: AxiosResponse<TOutput> | null = null;
  let currentStartPoint = params?.startPoint || '';
  const limit = maxItems;
  while (true) {
    const queryParams = createQueryStringParameters({ ...(params || {}), startPoint: currentStartPoint });
    const response = await fetcher(queryParams);
    lastResponse = response;
    const data = response.data;

    if (data && data.Items && data.Items.length > 0) {
      accumulatedItems.push(...data.Items);
      if (limit && accumulatedItems.length >= limit) {
        accumulatedItems = accumulatedItems.slice(0, limit);
        break;
      }
      if (data.HasNext && data.EndCursor) {
        currentStartPoint = data.EndCursor;
      }
      else {
        break;
      }
    }
    else {
      break;
    }
  }
  return {
    ...lastResponse,
    data: {
      Items: accumulatedItems

    }
  } as AxiosResponse<{ Items: TItemOutput[] }, any>;
};



export const createQueryStringParameters = (paramsRecord: Record<string, any> | undefined) => {
  let params: string[] = [];
  if (!paramsRecord) return "";
  for (let param of Object.keys(paramsRecord)) {
    if (
      paramsRecord[param] &&
      paramsRecord[param] !== -1 &&
      !param.startsWith("_")
    ) {
      params.push(`${param}=${paramsRecord[param]}`);
    }
  }
  return `?${params.join("&")}`;
};
